import type { Appointment, Extension, Patient } from '@medplum/fhirtypes';
import {
  CLIENT_LOCATION_CONFIRMATION_SUB_EXT,
  CLIENT_SESSION_LOCATION_CONFIRMATION_EXT,
} from '@unify/fhir-model/constants';
import type {
  ClientLocationConfirmation,
  ClientLocationConfirmationSource,
  LocationConfirmationInput,
} from './types';
import {
  LOCATION_ATTESTATION_TEXT,
  LOCATION_ATTESTATION_VERSION,
} from './constants';
import { checkLicensureForSessionLocation } from './licensureCheck';

function subExt(extensions: Extension[] | undefined, url: string): Extension | undefined {
  return extensions?.find((e) => e.url === url);
}

function subString(extensions: Extension[] | undefined, url: string): string | undefined {
  return subExt(extensions, url)?.valueString?.trim() || undefined;
}

function subBoolean(extensions: Extension[] | undefined, url: string): boolean {
  return subExt(extensions, url)?.valueBoolean === true;
}

function subDateTime(extensions: Extension[] | undefined, url: string): string | undefined {
  return subExt(extensions, url)?.valueDateTime;
}

function subReferenceId(extensions: Extension[] | undefined, url: string): string | undefined {
  const ref = subExt(extensions, url)?.valueReference?.reference;
  if (!ref) return undefined;
  const parts = ref.split('/');
  return parts.length === 2 ? parts[1] : undefined;
}

function locationConfirmationExtensions(appointment: Appointment | undefined): Extension[] {
  return (appointment?.extension ?? []).filter(
    (e) => e.url === CLIENT_SESSION_LOCATION_CONFIRMATION_EXT && (e.extension?.length ?? 0) > 0,
  );
}

function parseConfirmationFromExtension(
  root: Extension,
  appointmentId: string,
): ClientLocationConfirmation | null {
  const nested = root.extension;
  if (!nested?.length) return null;

  const street = subString(nested, CLIENT_LOCATION_CONFIRMATION_SUB_EXT.street);
  const city = subString(nested, CLIENT_LOCATION_CONFIRMATION_SUB_EXT.city);
  const state = subString(nested, CLIENT_LOCATION_CONFIRMATION_SUB_EXT.state);
  const postalCode = subString(nested, CLIENT_LOCATION_CONFIRMATION_SUB_EXT.postalCode);
  const confirmedAt = subDateTime(nested, CLIENT_LOCATION_CONFIRMATION_SUB_EXT.confirmedAt);
  const source = subString(nested, CLIENT_LOCATION_CONFIRMATION_SUB_EXT.source) as
    | ClientLocationConfirmationSource
    | undefined;
  const confirmedByPatientId = subReferenceId(nested, CLIENT_LOCATION_CONFIRMATION_SUB_EXT.confirmedByPatient);

  if (!street || !city || !state || !postalCode || !confirmedAt || !source || !confirmedByPatientId) {
    return null;
  }

  return {
    street,
    city,
    state,
    postalCode,
    confirmedAt,
    attestationText:
      subString(nested, CLIENT_LOCATION_CONFIRMATION_SUB_EXT.attestationText) ?? LOCATION_ATTESTATION_TEXT,
    attestationVersion:
      subString(nested, CLIENT_LOCATION_CONFIRMATION_SUB_EXT.attestationVersion) ?? LOCATION_ATTESTATION_VERSION,
    source,
    confirmedByPatientId,
    practitionerId: subReferenceId(nested, CLIENT_LOCATION_CONFIRMATION_SUB_EXT.practitionerId),
    wasEditedAfterPrefill: subBoolean(nested, CLIENT_LOCATION_CONFIRMATION_SUB_EXT.wasEditedAfterPrefill),
    ipDerivedCity: subString(nested, CLIENT_LOCATION_CONFIRMATION_SUB_EXT.ipDerivedCity),
    ipDerivedState: subString(nested, CLIENT_LOCATION_CONFIRMATION_SUB_EXT.ipDerivedState),
    stateMismatch: subBoolean(nested, CLIENT_LOCATION_CONFIRMATION_SUB_EXT.stateMismatch),
    licensureBlocking: subBoolean(nested, CLIENT_LOCATION_CONFIRMATION_SUB_EXT.licensureBlocking),
    notAtUsualAddress: subBoolean(nested, CLIENT_LOCATION_CONFIRMATION_SUB_EXT.notAtUsualAddress),
    appointmentId,
  };
}

/** All client location confirmations on the appointment (one repeating extension per participant). */
export function getClientLocationConfirmationsFromAppointment(
  appointment: Appointment | undefined,
): ClientLocationConfirmation[] {
  if (!appointment?.id) return [];
  return locationConfirmationExtensions(appointment)
    .map((ext) => parseConfirmationFromExtension(ext, appointment.id!))
    .filter((c): c is ClientLocationConfirmation => c !== null);
}

export function getClientLocationConfirmationForPatient(
  appointment: Appointment | undefined,
  patientId: string | undefined,
): ClientLocationConfirmation | null {
  if (!patientId) return null;
  return (
    getClientLocationConfirmationsFromAppointment(appointment).find(
      (c) => c.confirmedByPatientId === patientId,
    ) ?? null
  );
}

/** @deprecated Prefer getClientLocationConfirmationForPatient or getClientLocationConfirmationsFromAppointment. */
export function getClientLocationConfirmationFromAppointment(
  appointment: Appointment | undefined,
): ClientLocationConfirmation | null {
  const confirmations = getClientLocationConfirmationsFromAppointment(appointment);
  return confirmations[0] ?? null;
}

export function hasValidClientLocationConfirmation(
  appointment: Appointment | undefined,
  patientId?: string,
): boolean {
  if (patientId) {
    return getClientLocationConfirmationForPatient(appointment, patientId) !== null;
  }
  return getClientLocationConfirmationsFromAppointment(appointment).length > 0;
}

function resolveConfirmationSource(input: LocationConfirmationInput): ClientLocationConfirmationSource {
  if (input.prefillSource === 'device_location') return 'device_location_prefilled_client_attested';
  if (input.prefillSource === 'ip') return 'ip_prefilled_client_attested';
  if (input.prefillSource === 'saved_address') return 'saved_address_client_attested';
  return 'manual_client_attested';
}

function buildNestedExtensions(
  confirmation: ClientLocationConfirmation,
): Extension[] {
  const exts: Extension[] = [
    { url: CLIENT_LOCATION_CONFIRMATION_SUB_EXT.street, valueString: confirmation.street },
    { url: CLIENT_LOCATION_CONFIRMATION_SUB_EXT.city, valueString: confirmation.city },
    { url: CLIENT_LOCATION_CONFIRMATION_SUB_EXT.state, valueString: confirmation.state },
    { url: CLIENT_LOCATION_CONFIRMATION_SUB_EXT.postalCode, valueString: confirmation.postalCode },
    { url: CLIENT_LOCATION_CONFIRMATION_SUB_EXT.confirmedAt, valueDateTime: confirmation.confirmedAt },
    { url: CLIENT_LOCATION_CONFIRMATION_SUB_EXT.source, valueString: confirmation.source },
    {
      url: CLIENT_LOCATION_CONFIRMATION_SUB_EXT.attestationVersion,
      valueString: confirmation.attestationVersion,
    },
    { url: CLIENT_LOCATION_CONFIRMATION_SUB_EXT.attestationText, valueString: confirmation.attestationText },
    {
      url: CLIENT_LOCATION_CONFIRMATION_SUB_EXT.confirmedByPatient,
      valueReference: { reference: `Patient/${confirmation.confirmedByPatientId}` },
    },
    {
      url: CLIENT_LOCATION_CONFIRMATION_SUB_EXT.wasEditedAfterPrefill,
      valueBoolean: confirmation.wasEditedAfterPrefill,
    },
    { url: CLIENT_LOCATION_CONFIRMATION_SUB_EXT.stateMismatch, valueBoolean: confirmation.stateMismatch },
    { url: CLIENT_LOCATION_CONFIRMATION_SUB_EXT.licensureBlocking, valueBoolean: confirmation.licensureBlocking },
    {
      url: CLIENT_LOCATION_CONFIRMATION_SUB_EXT.notAtUsualAddress,
      valueBoolean: confirmation.notAtUsualAddress,
    },
  ];

  if (confirmation.ipDerivedCity) {
    exts.push({
      url: CLIENT_LOCATION_CONFIRMATION_SUB_EXT.ipDerivedCity,
      valueString: confirmation.ipDerivedCity,
    });
  }
  if (confirmation.ipDerivedState) {
    exts.push({
      url: CLIENT_LOCATION_CONFIRMATION_SUB_EXT.ipDerivedState,
      valueString: confirmation.ipDerivedState,
    });
  }
  if (confirmation.practitionerId) {
    exts.push({
      url: CLIENT_LOCATION_CONFIRMATION_SUB_EXT.practitionerId,
      valueReference: { reference: `Practitioner/${confirmation.practitionerId}` },
    });
  }

  return exts;
}

export function buildClientLocationConfirmation(
  appointment: Appointment,
  patientId: string,
  input: LocationConfirmationInput,
  opts?: { sourceOverride?: ClientLocationConfirmationSource },
): ClientLocationConfirmation {
  const source = opts?.sourceOverride ?? resolveConfirmationSource(input);
  const stateMismatch = Boolean(
    input.ipDerivedState &&
      input.ipDerivedState.trim().toUpperCase() !== input.state.trim().toUpperCase(),
  );

  const practitionerParticipant = appointment.participant?.find((p) =>
    p.actor?.reference?.startsWith('Practitioner/'),
  );
  const practitionerId = practitionerParticipant?.actor?.reference?.split('/')[1];

  const licensureResult = checkLicensureForSessionLocation({
    attestedState: input.state,
    practitionerId,
  });

  return {
    street: input.street.trim(),
    city: input.city.trim(),
    state: input.state.trim().toUpperCase(),
    postalCode: input.postalCode.trim(),
    confirmedAt: new Date().toISOString(),
    attestationText: LOCATION_ATTESTATION_TEXT,
    attestationVersion: LOCATION_ATTESTATION_VERSION,
    source,
    confirmedByPatientId: patientId,
    practitionerId,
    wasEditedAfterPrefill: input.wasEditedAfterPrefill,
    ipDerivedCity: input.ipDerivedCity?.trim(),
    ipDerivedState: input.ipDerivedState?.trim()?.toUpperCase(),
    stateMismatch,
    licensureBlocking: licensureResult.blocking,
    notAtUsualAddress: input.notAtUsualAddress,
    appointmentId: appointment.id ?? '',
  };
}

/** Upserts one participant's confirmation; preserves other participants' entries. */
export function applyClientLocationConfirmationToAppointment(
  appointment: Appointment,
  confirmation: ClientLocationConfirmation,
): Appointment {
  const others = (appointment.extension ?? []).filter((e) => {
    if (e.url !== CLIENT_SESSION_LOCATION_CONFIRMATION_EXT) return true;
    const patientId = subReferenceId(e.extension, CLIENT_LOCATION_CONFIRMATION_SUB_EXT.confirmedByPatient);
    return patientId !== confirmation.confirmedByPatientId;
  });
  return {
    ...appointment,
    extension: [
      ...others,
      {
        url: CLIENT_SESSION_LOCATION_CONFIRMATION_EXT,
        extension: buildNestedExtensions(confirmation),
      },
    ],
  };
}

export function patientHomeAddressPrefill(patient: Patient | undefined): {
  street: string;
  city: string;
  state: string;
  postalCode: string;
} | null {
  const home = patient?.address?.find((a) => a.use === 'home' || !a.use) ?? patient?.address?.[0];
  if (!home) return null;
  const street = home.line?.[0]?.trim() ?? '';
  const city = home.city?.trim() ?? '';
  const state = home.state?.trim()?.toUpperCase() ?? '';
  const postalCode = home.postalCode?.trim() ?? '';
  if (!street && !city && !state && !postalCode) return null;
  return { street, city, state, postalCode };
}

/** Single-line label for note attendance/location header and pickers. */
export function formatClientLocationConfirmationLabel(
  confirmation: Pick<ClientLocationConfirmation, 'street' | 'city' | 'state' | 'postalCode'>,
): string {
  const parts = [
    confirmation.street.trim(),
    confirmation.city.trim(),
    `${confirmation.state.trim()} ${confirmation.postalCode.trim()}`.trim(),
  ].filter(Boolean);
  return parts.join(', ');
}

/** Map patient id → location label from all Appointment client-location extensions. */
export function getClientLocationLabelsFromAppointment(
  appointment: Appointment | undefined,
): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const confirmation of getClientLocationConfirmationsFromAppointment(appointment)) {
    labels[confirmation.confirmedByPatientId] = formatClientLocationConfirmationLabel(confirmation);
  }
  return labels;
}
